/**
 * Landmark providers
 *
 * Decision ◇1 was "use the best possible ML model": a 21-point hand model
 * (MediaPipe Hands or a better current one) is the intended **primary** path,
 * and the classical segmentation in hand-geometry.js is the **fallback** for when
 * the model is unavailable or declined. Both produce the same `Landmarks` object,
 * so nothing downstream knows which ran.
 *
 * ⚠ **The ML model is not installed.** Nothing in `package.json` provides one and
 * ◇18 (self-host ~3 MB on our own origin, to keep `script-src 'self'` and the
 * offline promise) is still open. This file supplies everything *around* the
 * model — the registry, the fall-through, and `fromLandmarks21()`, which turns a
 * 21-point result into our `Landmarks` — so wiring a model in is registering one
 * function. `providerStatus()` reports honestly which path is live.
 *
 * © Manish Jagdish Thatte
 */

import { findLandmarks, segmentHand } from './hand-geometry.js';

const providers = new Map();

/** Register an ML provider: `async (image) => { points: [{x,y}×21] (pixels), handedness?: 'Left'|'Right', score? } | null`. */
export function registerProvider(name, fn) { providers.set(name, fn); }
export function unregisterProvider(name) { providers.delete(name); }

export function providerStatus() {
  return { ml: providers.has('ml') ? 'registered' : 'not-installed', classical: 'available', primary: providers.has('ml') ? 'ml' : 'classical' };
}

const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
const P = (pts, i) => [pts[i].x, pts[i].y];

/**
 * Convert MediaPipe's 21 hand landmarks (pixel coordinates) into our Landmarks.
 * Indices: 0 wrist · thumb 1–4 · index 5–8 · middle 9–12 · ring 13–16 · little 17–20.
 */
export function fromLandmarks21(points, { handedness = null } = {}) {
  if (!Array.isArray(points) || points.length !== 21) return { ok: false, reason: 'bad-landmarks' };
  const tips = [4, 8, 12, 16, 20].map((i) => P(points, i));
  const mcp = [5, 9, 13, 17].map((i) => P(points, i));
  const webs = [mid(P(points, 2), mcp[0]), mid(mcp[0], mcp[1]), mid(mcp[1], mcp[2]), mid(mcp[2], mcp[3])];
  const wrist = P(points, 0);
  const cx = (wrist[0] + mcp[0][0] + mcp[1][0] + mcp[2][0] + mcp[3][0]) / 5; const cy = (wrist[1] + mcp[0][1] + mcp[1][1] + mcp[2][1] + mcp[3][1]) / 5;
  const webMid = [(webs[1][0] + webs[2][0] + webs[3][0]) / 3, (webs[1][1] + webs[2][1] + webs[3][1]) / 3];
  let fx = webMid[0] - wrist[0]; let fy = webMid[1] - wrist[1]; const fl = Math.hypot(fx, fy) || 1; fx /= fl; fy /= fl;
  let nx = mcp[3][0] - mcp[0][0]; let ny = mcp[3][1] - mcp[0][1];
  const pr = nx * fx + ny * fy; nx -= pr * fx; ny -= pr * fy; const nl = Math.hypot(nx, ny) || 1; nx /= nl; ny /= nl;
  const palmWidth = Math.hypot(mcp[3][0] - mcp[0][0], mcp[3][1] - mcp[0][1]) * 1.2;
  // the thumb must lie on the −n side; if the model's ordering says otherwise, flip so u still runs thumb → ulnar
  const thumbSide = (tips[0][0] - cx) * nx + (tips[0][1] - cy) * ny;
  if (thumbSide > 0) { nx = -nx; ny = -ny; }
  return {
    ok: true, reason: null, source: 'ml', centre: [cx, cy], radius: palmWidth / 2.4, palmWidth, tips, webs, wrist,
    fingerDir: [fx, fy], thumbSideDir: [-nx, -ny], ulnarDir: [nx, ny], webMid, confidence: 0.9, modelHandedness: handedness
  };
}

/**
 * Get landmarks, preferring the model and falling back to the classical path.
 * Returns `{ landmarks, mask?, source, fellBack, reason? }`. The classical path also yields the hand mask.
 */
export async function getLandmarks(image, { balanced = image, prefer = 'ml' } = {}) {
  if (prefer === 'ml' && providers.has('ml')) {
    try {
      const out = await providers.get('ml')(image);
      if (out?.points) {
        const lm = fromLandmarks21(out.points, { handedness: out.handedness });
        if (lm.ok) {
          const seg = segmentHand(balanced);          // the mask is still wanted for finger-shape measurements
          return { landmarks: lm, mask: seg.ok ? seg.mask : null, segmentation: seg, source: 'ml', fellBack: false };
        }
      }
    } catch (e) {
      return classical([image, balanced], `ml-failed: ${e?.message ?? e}`);
    }
    return classical([image, balanced], 'ml-found-no-hand');
  }
  return classical([image, balanced], prefer === 'ml' ? 'ml-not-installed' : null);
}

/**
 * Classical path. Segmentation is tried on the image as it is first and on the
 * grey-world balanced copy second. Grey-world assumes the scene averages to grey,
 * which is exactly what a frame full of hand does not — balancing it can turn the
 * skin grey — yet under warm indoor light it is what rescues the chroma test. So
 * neither is trusted alone.
 */
function classical(images, why) {
  let first = null;
  for (const img of images) {
    const seg = segmentHand(img);
    let res;
    if (!seg.ok) res = { landmarks: null, mask: null, segmentation: seg, source: 'classical', fellBack: !!why, reason: seg.reason, stage: 'segmentation' };
    else {
      const lm = findLandmarks(seg.mask);
      res = lm.ok
        ? { landmarks: { ...lm, source: 'classical' }, mask: seg.mask, segmentation: seg, source: 'classical', fellBack: !!why, fallbackReason: why }
        : { landmarks: null, mask: seg.mask, segmentation: seg, source: 'classical', fellBack: !!why, reason: lm.reason, stage: 'landmarks' };
    }
    if (res.landmarks) return res;
    // keep the most informative failure: one that got furthest (landmarks > segmentation)
    if (!first || (res.stage === 'landmarks' && first.stage === 'segmentation')) first = res;
  }
  return first;
}
